import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage'
import { doc, getDoc, setDoc } from 'firebase/firestore'
import { storage, db, shouldSkipFirestore } from './config'
import { getCurrentUser } from './auth'
import { logger } from '@/utils/logger'
import { errorHandler } from '@/utils/errorHandler'

const FAVICON_CONFIG_DOC = 'config'
const FAVICON_CONFIG_FIELD = 'favicon'
const STORAGE_PATH = 'favicon/favicon'

/**
 * Lädt ein Favicon hoch und speichert die URL in Firestore
 */
export const uploadFavicon = async (file: File): Promise<string> => {
  if (!db) {
    throw new Error('Firestore ist nicht initialisiert')
  }
  
  const user = getCurrentUser()
  if (!user) {
    throw new Error('Benutzer ist nicht authentifiziert. Bitte melden Sie sich an.')
  }

  // Stelle sicher, dass wir ein frisches Token haben
  try {
    await user.getIdToken(true)
  } catch (error) {
    logger.error('Fehler beim Aktualisieren des Auth-Tokens', error)
    throw new Error('Authentifizierung fehlgeschlagen. Bitte melden Sie sich erneut an.')
  }

  // Bestimme die Dateiendung
  const fileExtension = file.name.split('.').pop()?.toLowerCase() || 'png'
  
  // Erstelle den Storage-Pfad mit korrekter Extension
  const fileName = `${STORAGE_PATH}.${fileExtension}`
  const storageRef = ref(storage, fileName)

  // Lösche das alte Favicon, falls vorhanden
  try {
    await deleteObject(storageRef)
  } catch (error) {
    // Ignoriere Fehler, wenn das alte Favicon nicht existiert
    logger.debug('Altes Favicon nicht gefunden oder bereits gelöscht')
  }

  // Lade das neue Favicon hoch
  await uploadBytes(storageRef, file)
  const downloadURL = await getDownloadURL(storageRef)

  // Speichere die URL in Firestore
  const configRef = doc(db, FAVICON_CONFIG_DOC, 'site')
  await setDoc(
    configRef,
    {
      [FAVICON_CONFIG_FIELD]: downloadURL,
      updatedAt: new Date().toISOString()
    },
    { merge: true }
  )

  return downloadURL
}

/**
 * Ruft die aktuelle Favicon-URL aus Firestore ab
 */
export const getFaviconUrl = async (): Promise<string | null> => {
  if (!db) {
    throw new Error('Firestore ist nicht initialisiert')
  }
  
  try {
    const configRef = doc(db, FAVICON_CONFIG_DOC, 'site')
    const docSnap = await getDoc(configRef)
    
    if (docSnap.exists()) {
      const data = docSnap.data()
      return data[FAVICON_CONFIG_FIELD] || null
    }
    
    return null
  } catch (error) {
    errorHandler.handle(error, 'getFaviconUrl')
    return null
  }
}

/**
 * Löscht das Favicon aus Storage und Firestore
 */
export const deleteFavicon = async (): Promise<void> => {
  if (!db) {
    throw new Error('Firestore ist nicht initialisiert')
  }
  
  const user = getCurrentUser()
  if (!user) {
    throw new Error('Benutzer ist nicht authentifiziert. Bitte melden Sie sich an.')
  }

  try {
    // Versuche verschiedene Dateiformate zu löschen
    const extensions = ['ico', 'png', 'svg', 'jpg', 'jpeg', 'webp']
    const deletePromises = extensions.map(async (ext) => {
      try {
        const storageRef = ref(storage, `${STORAGE_PATH}.${ext}`)
        await deleteObject(storageRef)
      } catch (error) {
        // Ignoriere Fehler, wenn die Datei nicht existiert
      }
    })

    await Promise.all(deletePromises)

    // Entferne die URL aus Firestore
    const configRef = doc(db, FAVICON_CONFIG_DOC, 'site')
    await setDoc(
      configRef,
      {
        [FAVICON_CONFIG_FIELD]: null,
        updatedAt: new Date().toISOString()
      },
      { merge: true }
    )
  } catch (error) {
    errorHandler.handle(error, 'deleteFavicon')
    throw error
  }
}

/**
 * Öffentliche Funktion: Ruft die Favicon-URL ab (ohne Authentifizierung)
 * Diese Funktion kann beim App-Start verwendet werden
 */
export const getPublicFaviconUrl = async (): Promise<string | null> => {
  // Für Bots und Performance-Tools: Keine Firestore-Abfrage, um Timeouts zu vermeiden
  if (shouldSkipFirestore || !db) {
    logger.debug('Firestore übersprungen - Favicon wird nicht geladen');
    return null;
  }
  
  try {
    const configRef = doc(db, FAVICON_CONFIG_DOC, 'site')
    const docSnap = await getDoc(configRef)
    
    if (docSnap.exists()) {
      const data = docSnap.data()
      return data[FAVICON_CONFIG_FIELD] || null
    }
    
    return null
  } catch (error) {
    logger.error('Fehler beim Abrufen des öffentlichen Favicons', error)
    return null
  }
}







